import { DateTime } from 'luxon';

import { DateConstraints, PALLET_STATES, PalletState, TransferablesFilters } from './types';

const parseIdParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const parseDateInput = (value: string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

const isPalletState = (value: string): value is PalletState => (PALLET_STATES as readonly string[]).includes(value);

export const formatFilterDateValue = (date: Date | undefined): string => {
  if (!date) {
    return '';
  }
  const parsed = DateTime.fromJSDate(date);
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : '';
};

export const defaultFilters = (): TransferablesFilters => {
  const now = DateTime.now();
  return {
    from: now.toJSDate(),
    till: now.plus({ days: 3 }).toJSDate(),
  };
};

export const parseFiltersFromSearch = (search: string): TransferablesFilters => {
  const params = new URLSearchParams(search);
  const filters: TransferablesFilters = {};

  const regionID = parseIdParam(params.get('region_id'));
  if (regionID !== undefined) {
    filters.region_id = regionID;
  }

  const warehouseID = parseIdParam(params.get('warehouse_id'));
  if (warehouseID !== undefined) {
    filters.warehouse_id = warehouseID;
  }

  const state = params.get('state');
  if (state && isPalletState(state)) {
    filters.state = state;
  }

  const from = parseDateInput(params.get('from'));
  const till = parseDateInput(params.get('till'));
  const defaults = defaultFilters();

  if (from || till) {
    filters.from = from ?? defaults.from;
    filters.till = till ?? defaults.till;
  } else {
    Object.assign(filters, defaults);
  }

  return filters;
};

export const cleanupFilters = (filters: TransferablesFilters): Record<string, string | number> => {
  const results: Record<string, string | number> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value instanceof Date) {
      results[key] = DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      results[key] = value as string | number;
    }
  });

  return results;
};

export const syncFiltersToUrl = (filters: TransferablesFilters) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();

  Object.entries(cleanupFilters(filters)).forEach(([key, value]) => {
    searchParams.set(key, String(value));
  });

  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

export const buildDateConstraints = (filters: TransferablesFilters): DateConstraints => {
  const now = DateTime.now();
  const from = filters.from ? DateTime.fromJSDate(filters.from) : undefined;
  const till = filters.till ? DateTime.fromJSDate(filters.till) : undefined;

  return {
    from: {
      min: till ? till.minus({ days: 5 }).toJSDate() : now.minus({ months: 2 }).toJSDate(),
      max: till ? till.toJSDate() : now.plus({ months: 2 }).toJSDate(),
    },
    till: {
      min: from ? from.toJSDate() : now.minus({ months: 2 }).toJSDate(),
      max: from ? from.plus({ days: 5 }).toJSDate() : now.plus({ months: 2 }).toJSDate(),
    },
  };
};
